/**
 * WIZARD_RightToLeft_v05 (React + TypeScript + React-Bootstrap)
 * Version: v05
 * Date: 2025-09-05 (America/Denver)
 *
 * v05:
 * - "Missing/invalid fields" tooltip shown to the RIGHT of the form (not on the Finish button)
 * - Finish is gray/disabled until valid; turns green when ready
 * - Required fields marked; field-level green ✓ when valid
 * - Help text effect plays once per slide, then remains static
 * - Greys out during submit; summary after success
 */

import React, {
  useMemo,
  useState,
  useCallback,
  PropsWithChildren,
  useEffect,
  useRef,
} from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Carousel,
  Col,
  Container,
  Form,
  InputGroup,
  ProgressBar,
  Row,
  Spinner,
  Overlay,
  Tooltip,
} from "react-bootstrap";

/* ============================================================
   Declarations (tunable variables & types)
   ============================================================ */
const VERSION = "v05";
const SHOW_VERSION = true; // toggle version badge on/off
const TEST_MODE = true; // true = console.log payload; false = POST to API
const API_GATEWAY_URL =
  "https://your-api-id.execute-api.us-east-1.amazonaws.com/prod/submit"; // <-- replace

const SLIDE_ANIMATION_MS = 450; // carousel slide duration
const SHOW_PROGRESS_BAR = true;

// --- Help text effect controls ---
type HelpEffect = "none" | "typewriter" | "fade";
/** Choose the global help text effect (runs once per slide) */
const HELP_EFFECT: HelpEffect = "typewriter"; // "none" | "typewriter" | "fade"
const TYPEWRITER_SPEED_MS = 20;
const TYPEWRITER_CURSOR = true;
const FADE_DURATION_MS = 400;

const LABELS = {
  title: "New Policy Wizard",
  subtitle: "Tell us about you and your business",
  stepTitles: ["Contact", "Business", "Operations", "Coverage", "Summary"],
  buttons: {
    back: "Back",
    next: "Next",
    finish: "Finish",
    edit: "Edit",
    submit: "Submit",
  },
  notices: {
    required: "Required fields are marked with *",
  },
} as const;

const STEP_HELP = [
  {
    title: "Why your contact details matter",
    text:
      "Accurate contact info lets us reach you with quotes, clarifications, and bindable offers without delay.",
  },
  {
    title: "Why we need business details",
    text:
      "Underwriters price risk based on your business profile (years active, structure, employees). Clear data yields accurate pricing.",
  },
  {
    title: "Operational risk questions",
    text:
      "Insurers consider where and how you work (residential vs. commercial vs. industrial) to tailor coverage and premium.",
  },
  {
    title: "Coverage preferences",
    text:
      "Your limits, deductibles, and additional coverages determine pricing and ensure your policy fits real exposures.",
  },
] as const;

/* ============================================================
   Types & defaults
   ============================================================ */
type WizardData = {
  fullName: string;
  email: string;
  phone: string;

  businessName: string;
  yearsInBusiness: string;
  employees: string;

  pctResidential: string;
  pctCommercial: string;
  pctIndustrial: string;

  generalLiabilityLimit: string;
  deductible: string;
  toolsAndEquipmentCoverage: string; // optional
};

const defaultData: WizardData = {
  fullName: "",
  email: "",
  phone: "",

  businessName: "",
  yearsInBusiness: "",
  employees: "",

  pctResidential: "",
  pctCommercial: "",
  pctIndustrial: "",

  generalLiabilityLimit: "",
  deductible: "",
  toolsAndEquipmentCoverage: "",
};

/* ============================================================
   Effects (typewriter/fade)
   ============================================================ */
const TypewriterText: React.FC<{
  text: string;
  speedMs?: number;
  showCursor?: boolean;
  triggerKey?: string | number;
  className?: string;
}> = ({
  text,
  speedMs = TYPEWRITER_SPEED_MS,
  showCursor = TYPEWRITER_CURSOR,
  triggerKey,
  className,
}) => {
  const [idx, setIdx] = useState<number>(0);
  const tRef = useRef<number | null>(null);

  useEffect(() => {
    setIdx(0);
  }, [triggerKey, text]);

  useEffect(() => {
    if (idx >= text.length) return;
    tRef.current = window.setTimeout(() => setIdx((i) => i + 1), speedMs);
    return () => {
      if (tRef.current) window.clearTimeout(tRef.current);
    };
  }, [idx, text.length, speedMs]);

  const done = idx >= text.length;
  return (
    <span className={className}>
      {text.slice(0, idx)}
      {showCursor && !done ? <span className="tw-cursor">|</span> : null}
    </span>
  );
};

const FadeText: React.FC<{
  text: string;
  durationMs?: number;
  triggerKey?: string | number;
  className?: string;
}> = ({ text, durationMs = FADE_DURATION_MS, triggerKey, className }) => {
  const [visible, setVisible] = useState<boolean>(false);
  useEffect(() => {
    setVisible(false);
    const t = window.setTimeout(() => setVisible(true), 10);
    return () => window.clearTimeout(t);
  }, [triggerKey, text]);

  return (
    <span
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transition: `opacity ${durationMs}ms ease`,
        display: "inline-block",
      }}
    >
      {text}
    </span>
  );
};

/* ============================================================
   UI helpers
   ============================================================ */
type HelpPanelProps = {
  title: string;
  text: string;
  className?: string;
  effect?: HelpEffect;
  triggerKey?: string | number;
};
const HelpPanel: React.FC<HelpPanelProps> = ({
  title,
  text,
  className,
  effect = "none",
  triggerKey,
}) => (
  <Alert variant="info" className={`mb-3 ${className ?? ""}`.trim()}>
    <div className="fw-semibold mb-1">{title}</div>
    <div className="small text-muted">
      {effect === "typewriter" ? (
        <TypewriterText text={text} triggerKey={triggerKey} />
      ) : effect === "fade" ? (
        <FadeText text={text} triggerKey={triggerKey} />
      ) : (
        text
      )}
    </div>
  </Alert>
);

type FieldNoteProps = PropsWithChildren<{ className?: string }>;
const FieldNote: React.FC<FieldNoteProps> = ({ children, className }) => (
  <div className={`form-text ${className ?? ""}`.trim()}>{children}</div>
);

/** Utility validations */
const isNumeric = (s: string) => /^\d+(\.\d+)?$/.test(s.trim());
const isEmail = (s: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(s.trim());
const digitsOnly = (s: string) => s.replace(/\D/g, "");

/* ============================================================
   Validation map (field-wise)
   ============================================================ */
type ValidMap = Record<keyof WizardData, boolean>;
type ErrorItem = { field: keyof WizardData; label: string; reason: string };

const FIELD_LABEL: Record<keyof WizardData, string> = {
  fullName: "Full Name",
  email: "Email",
  phone: "Phone",
  businessName: "Business Name",
  yearsInBusiness: "Years in Business",
  employees: "Employees",
  pctResidential: "% Residential",
  pctCommercial: "% Commercial",
  pctIndustrial: "% Industrial",
  generalLiabilityLimit: "General Liability Limit",
  deductible: "Deductible",
  toolsAndEquipmentCoverage: "Tools & Equipment Coverage",
};

function buildValidity(data: WizardData): { valid: ValidMap; errors: ErrorItem[]; sumOk: boolean } {
  const valid: ValidMap = {
    fullName: data.fullName.trim().length > 1,
    email: isEmail(data.email),
    phone: digitsOnly(data.phone).length >= 7,
    businessName: data.businessName.trim().length > 1,
    yearsInBusiness: isNumeric(data.yearsInBusiness),
    employees: isNumeric(data.employees),
    pctResidential: isNumeric(data.pctResidential),
    pctCommercial: isNumeric(data.pctCommercial),
    pctIndustrial: isNumeric(data.pctIndustrial),
    generalLiabilityLimit: !!data.generalLiabilityLimit,
    deductible: !!data.deductible,
    toolsAndEquipmentCoverage: true, // optional
  };

  const pctR = parseFloat(data.pctResidential || "0");
  const pctC = parseFloat(data.pctCommercial || "0");
  const pctI = parseFloat(data.pctIndustrial || "0");
  const sumOk =
    valid.pctResidential &&
    valid.pctCommercial &&
    valid.pctIndustrial &&
    Math.round(pctR + pctC + pctI) === 100;

  const errors: ErrorItem[] = [];
  (Object.keys(valid) as (keyof WizardData)[]).forEach((k) => {
    if (!valid[k]) {
      let reason = "missing or invalid";
      if (k === "email") reason = "invalid email";
      if (k === "phone") reason = "phone too short";
      if (k === "yearsInBusiness" || k === "employees") reason = "must be a number";
      if (k === "pctResidential" || k === "pctCommercial" || k === "pctIndustrial")
        reason = "must be a number";
      if (k === "generalLiabilityLimit" || k === "deductible") reason = "required";
      if (k === "fullName" || k === "businessName") reason = "required";
      errors.push({ field: k, label: FIELD_LABEL[k], reason });
    }
  });

  if (!sumOk) {
    errors.push({
      field: "pctResidential",
      label: "Work % Mix",
      reason: "Residential + Commercial + Industrial must equal 100",
    });
  }

  return { valid, errors, sumOk };
}

/* ============================================================
   Validated Control wrapper (adds ✓ when valid)
   ============================================================ */
const ValidWrap: React.FC<{ valid: boolean; children: React.ReactNode }> = ({
  valid,
  children,
}) => (
  <InputGroup>
    {children}
    {valid && <InputGroup.Text aria-label="valid">✓</InputGroup.Text>}
  </InputGroup>
);

/* ============================================================
   Step subcomponents (modular) — helpEffect runs once per slide
   ============================================================ */
type StepProps = {
  data: WizardData;
  setData: (partial: Partial<WizardData>) => void;
  helpEffect: HelpEffect;
  helpKey: string | number;
  valid: ValidMap;
};

const Step1Contact: React.FC<StepProps> = ({
  data,
  setData,
  helpEffect,
  helpKey,
  valid,
}) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[0].title}
        text={STEP_HELP[0].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Form.Group className="mb-3">
        <Form.Label>Full Name *</Form.Label>
        <ValidWrap valid={valid.fullName}>
          <Form.Control
            value={data.fullName}
            onChange={(e) => setData({ fullName: e.target.value })}
            placeholder="Jane Doe"
            required
            className={valid.fullName ? "is-valid" : data.fullName ? "is-invalid" : ""}
          />
        </ValidWrap>
        <FieldNote>We’ll reference this on your quote and policy docs.</FieldNote>
      </Form.Group>

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Email *</Form.Label>
            <ValidWrap valid={valid.email}>
              <Form.Control
                type="email"
                value={data.email}
                onChange={(e) => setData({ email: e.target.value })}
                placeholder="name@example.com"
                required
                className={valid.email ? "is-valid" : data.email ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>Used for sending quotes and policy updates.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Phone *</Form.Label>
            <ValidWrap valid={valid.phone}>
              <Form.Control
                value={data.phone}
                onChange={(e) => setData({ phone: e.target.value })}
                placeholder="(555) 555-5555"
                required
                className={valid.phone ? "is-valid" : data.phone ? "is-invalid" : ""}
              />
            </ValidWrap>
            <FieldNote>We’ll call/text for time-sensitive items.</FieldNote>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step2Business: React.FC<StepProps> = ({
  data,
  setData,
  helpEffect,
  helpKey,
  valid,
}) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[1].title}
        text={STEP_HELP[1].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Form.Group className="mb-3">
        <Form.Label>Business Name *</Form.Label>
        <ValidWrap valid={valid.businessName}>
          <Form.Control
            value={data.businessName}
            onChange={(e) => setData({ businessName: e.target.value })}
            placeholder="Acme Services LLC"
            required
            className={
              valid.businessName ? "is-valid" : data.businessName ? "is-invalid" : ""
            }
          />
        </ValidWrap>
        <FieldNote>Exactly as shown on your license/docs when possible.</FieldNote>
      </Form.Group>

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Years in Business *</Form.Label>
            <ValidWrap valid={valid.yearsInBusiness}>
              <Form.Control
                value={data.yearsInBusiness}
                onChange={(e) => setData({ yearsInBusiness: e.target.value })}
                placeholder="e.g., 5"
                required
                className={
                  valid.yearsInBusiness
                    ? "is-valid"
                    : data.yearsInBusiness
                    ? "is-invalid"
                    : ""
                }
              />
            </ValidWrap>
            <FieldNote>More tenure can improve pricing with some carriers.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Employees *</Form.Label>
            <ValidWrap valid={valid.employees}>
              <Form.Control
                value={data.employees}
                onChange={(e) => setData({ employees: e.target.value })}
                placeholder="e.g., 8"
                required
                className={
                  valid.employees ? "is-valid" : data.employees ? "is-invalid" : ""
                }
              />
            </ValidWrap>
            <FieldNote>Include owners if they perform field work.</FieldNote>
          </Form.Group>
        </Col>
      </Row>
    </Card.Body>
  </Card>
);

const Step3Operations: React.FC<StepProps> = ({
  data,
  setData,
  helpEffect,
  helpKey,
  valid,
}) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[2].title}
        text={STEP_HELP[2].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Row className="g-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>% Residential *</Form.Label>
            <ValidWrap valid={valid.pctResidential}>
              <Form.Control
                value={data.pctResidential}
                onChange={(e) => setData({ pctResidential: e.target.value })}
                placeholder="e.g., 60"
                required
                className={
                  valid.pctResidential
                    ? "is-valid"
                    : data.pctResidential
                    ? "is-invalid"
                    : ""
                }
              />
              <InputGroup.Text>%</InputGroup.Text>
            </ValidWrap>
            <FieldNote>Typical home projects, service calls, etc.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>% Commercial *</Form.Label>
            <ValidWrap valid={valid.pctCommercial}>
              <Form.Control
                value={data.pctCommercial}
                onChange={(e) => setData({ pctCommercial: e.target.value })}
                placeholder="e.g., 30"
                required
                className={
                  valid.pctCommercial
                    ? "is-valid"
                    : data.pctCommercial
                    ? "is-invalid"
                    : ""
                }
              />
              <InputGroup.Text>%</InputGroup.Text>
            </ValidWrap>
            <FieldNote>Retail/office/restaurant sites.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>% Industrial *</Form.Label>
            <ValidWrap valid={valid.pctIndustrial}>
              <Form.Control
                value={data.pctIndustrial}
                onChange={(e) => setData({ pctIndustrial: e.target.value })}
                placeholder="e.g., 10"
                required
                className={
                  valid.pctIndustrial
                    ? "is-valid"
                    : data.pctIndustrial
                    ? "is-invalid"
                    : ""
                }
              />
              <InputGroup.Text>%</InputGroup.Text>
            </ValidWrap>
            <FieldNote>Factories, plants, heavy facilities.</FieldNote>
          </Form.Group>
        </Col>
      </Row>

      <div className="small text-muted mt-2">
        Tip: Ensure the three percentages sum to 100%.
      </div>
    </Card.Body>
  </Card>
);

const Step4Coverage: React.FC<StepProps> = ({
  data,
  setData,
  helpEffect,
  helpKey,
  valid,
}) => (
  <Card className="shadow-sm">
    <Card.Body>
      <HelpPanel
        title={STEP_HELP[3].title}
        text={STEP_HELP[3].text}
        effect={helpEffect}
        triggerKey={helpKey}
      />

      <Row className="g-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>General Liability Limit *</Form.Label>
            <ValidWrap valid={valid.generalLiabilityLimit}>
              <Form.Select
                value={data.generalLiabilityLimit}
                onChange={(e) => setData({ generalLiabilityLimit: e.target.value })}
                required
                className={
                  valid.generalLiabilityLimit
                    ? "is-valid"
                    : data.generalLiabilityLimit
                    ? "is-invalid"
                    : ""
                }
              >
                <option value="">Select a limit…</option>
                <option>$1M / $2M</option>
                <option>$2M / $2M</option>
                <option>$2M / $4M</option>
              </Form.Select>
            </ValidWrap>
            <FieldNote>Common small-business options shown here.</FieldNote>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Deductible *</Form.Label>
            <ValidWrap valid={valid.deductible}>
              <Form.Select
                value={data.deductible}
                onChange={(e) => setData({ deductible: e.target.value })}
                required
                className={
                  valid.deductible ? "is-valid" : data.deductible ? "is-invalid" : ""
                }
              >
                <option value="">Select a deductible…</option>
                <option>$500</option>
                <option>$1,000</option>
                <option>$2,500</option>
                <option>$5,000</option>
              </Form.Select>
            </ValidWrap>
            <FieldNote>Higher deductibles often reduce premium.</FieldNote>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mt-3">
        <Form.Label>Tools & Equipment Coverage (desired limit)</Form.Label>
        <ValidWrap valid={true /* optional */}>
          <Form.Control
            value={data.toolsAndEquipmentCoverage}
            onChange={(e) =>
              setData({ toolsAndEquipmentCoverage: e.target.value })
            }
            placeholder="e.g., $25,000"
          />
        </ValidWrap>
        <FieldNote>Optional—covers theft/damage to your gear.</FieldNote>
      </Form.Group>
    </Card.Body>
  </Card>
);

/* ============================================================
   Summary (post-submit)
   ============================================================ */
const SummaryView: React.FC<{ data: WizardData }> = ({ data }) => (
  <Card className="shadow-sm">
    <Card.Body>
      <h5 className="mb-3">Summary</h5>
      <Row className="g-3">
        <Col md={6}>
          <h6 className="text-muted">Contact</h6>
          <div><strong>Name:</strong> {data.fullName || "-"}</div>
          <div><strong>Email:</strong> {data.email || "-"}</div>
          <div><strong>Phone:</strong> {data.phone || "-"}</div>
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Business</h6>
          <div><strong>Business Name:</strong> {data.businessName || "-"}</div>
          <div><strong>Years in Business:</strong> {data.yearsInBusiness || "-"}</div>
          <div><strong>Employees:</strong> {data.employees || "-"}</div>
        </Col>
      </Row>
      <hr />
      <Row className="g-3">
        <Col md={6}>
          <h6 className="text-muted">Operations</h6>
          <div><strong>% Residential:</strong> {data.pctResidential || "-"}</div>
          <div><strong>% Commercial:</strong> {data.pctCommercial || "-"}</div>
          <div><strong>% Industrial:</strong> {data.pctIndustrial || "-"}</div>
        </Col>
        <Col md={6}>
          <h6 className="text-muted">Coverage</h6>
          <div><strong>GL Limit:</strong> {data.generalLiabilityLimit || "-"}</div>
          <div><strong>Deductible:</strong> {data.deductible || "-"}</div>
          <div><strong>Tools & Equipment:</strong> {data.toolsAndEquipmentCoverage || "-"}</div>
        </Col>
      </Row>
      <Alert variant="secondary" className="mt-3 mb-0">
        Thanks! We’ll review and follow up if anything else is needed.
      </Alert>
    </Card.Body>
  </Card>
);

/* ============================================================
   Main Component
   ============================================================ */
export const WIZARD_RightToLeft_v05: React.FC = () => {
  // activeIndex: 0..3 are data-entry slides, 4 is Summary
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [data, setData] = useState<WizardData>(defaultData);

  // Track which steps already animated their help text
  const prevIndexRef = useRef<number | null>(null);
  const helpPlayedRef = useRef<[boolean, boolean, boolean, boolean]>([
    false,
    false,
    false,
    false,
  ]);

  useEffect(() => {
    const prev = prevIndexRef.current;
    if (prev !== null && prev >= 0 && prev <= 3) {
      helpPlayedRef.current[prev] = true;
    }
    prevIndexRef.current = activeIndex;
  }, [activeIndex]);

  // right-side tooltip anchor
  const rightTipAnchorRef = useRef<HTMLSpanElement | null>(null);

  // Shallow merge setter
  const patch = useCallback(
    (partial: Partial<WizardData>) => setData((prev) => ({ ...prev, ...partial })),
    []
  );

  // validity & errors
  const { valid, errors, sumOk } = useMemo(() => buildValidity(data), [data]);

  const requiredOk =
    valid.fullName &&
    valid.email &&
    valid.phone &&
    valid.businessName &&
    valid.yearsInBusiness &&
    valid.employees &&
    valid.pctResidential &&
    valid.pctCommercial &&
    valid.pctIndustrial &&
    valid.generalLiabilityLimit &&
    valid.deductible &&
    sumOk;

  const atFirst = activeIndex === 0;
  const atLastDataStep = activeIndex === 3;
  const atSummary = activeIndex === 4;

  const progress = useMemo(
    () => Math.round(((activeIndex + 1) / 5) * 100),
    [activeIndex]
  );

  const handlePrev = () => {
    if (activeIndex > 0) setActiveIndex((i) => i - 1);
  };
  const handleNext = () => {
    if (activeIndex < 3) setActiveIndex((i) => i + 1);
  };

  const handleFinish = async () => {
    setSubmitError(null);

    if (!requiredOk) {
      // Nothing to do; UI already shows right-side tooltip while invalid.
      return;
    }

    setSubmitting(true);
    try {
      const wire = {
        ...data,
        meta: {
          testMode: TEST_MODE,
          version: VERSION,
          submittedAtISO: new Date().toISOString(),
        },
      };

      if (TEST_MODE) {
        await new Promise((r) => setTimeout(r, 600));
        // eslint-disable-next-line no-console
        console.log("[TEST_MODE] Submitting payload:", wire);
      } else {
        const res = await fetch(API_GATEWAY_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(wire),
        });
        if (!res.ok) {
          const txt = await res.text().catch(() => "");
          throw new Error(
            `API error ${res.status}${txt ? `: ${txt.slice(0, 200)}` : ""}`
          );
        }
      }

      setActiveIndex(4); // Summary on success
    } catch (err: any) {
      setSubmitError(err?.message ?? "Unknown error submitting form.");
    } finally {
      setSubmitting(false);
    }
  };

  // Determine help effect for current slide (only first appearance)
  const effectiveHelp: HelpEffect =
    helpPlayedRef.current[activeIndex as 0 | 1 | 2 | 3] ? "none" : HELP_EFFECT;

  const stepCards = [
    <Step1Contact
      key="s1"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step2Business
      key="s2"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step3Operations
      key="s3"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <Step4Coverage
      key="s4"
      data={data}
      setData={patch}
      helpEffect={effectiveHelp}
      helpKey={`help-${activeIndex}`}
      valid={valid}
    />,
    <SummaryView key="s5" data={data} />,
  ];

  // Right-side tooltip content (always visible while invalid & not submitting)
  const tooltipContent = (
    <>
      <div className="mb-1 fw-semibold">Please fix:</div>
      <ul className="mb-0 ps-3">
        {errors.map((e, i) => (
          <li key={i}>
            {e.label} — {e.reason}
          </li>
        ))}
        {!sumOk && <li>Work % Mix — must total 100</li>}
      </ul>
    </>
  );

  return (
    <Container className="py-4 position-relative">
      {/* Header */}
      <Row className="mb-3 align-items-center">
        <Col>
          <h3 className="mb-0">{LABELS.title}</h3>
          <div className="text-muted">{LABELS.subtitle}</div>
        </Col>
        <Col className="text-end">
          {SHOW_VERSION && (
            <Badge bg="secondary" pill>
              {VERSION}
            </Badge>
          )}
          {/* Anchor for right-side tooltip */}
          <span ref={rightTipAnchorRef} className="ms-2 d-inline-block" />
        </Col>
      </Row>

      <Row>
        {/* Main form column */}
        <Col lg={9}>
          <Row className="mb-2">
            <Col>
              <div className="small text-muted">{LABELS.notices.required}</div>
            </Col>
          </Row>

          {SHOW_PROGRESS_BAR && (
            <Row className="mb-3">
              <Col>
                <ProgressBar now={progress} label={`${progress}%`} />
              </Col>
            </Row>
          )}

          {/* Step headline */}
          <Row className="mb-2">
            <Col>
              <h5 className="mb-0">
                {LABELS.stepTitles[activeIndex] ?? "Step"}
              </h5>
            </Col>
          </Row>

          {/* Submission grey-out wrapper */}
          <div className={submitting ? "wizard-submitting" : ""} aria-busy={submitting}>
            {/* Slides */}
            <Carousel
              activeIndex={activeIndex}
              onSelect={() => {}}
              interval={undefined}
              controls={false}
              indicators={false}
              slide
              fade={false}
              className="mb-3"
            >
              {stepCards.map((card, i) => (
                <Carousel.Item
                  key={`slide-${i}`}
                  style={{ transitionDuration: `${SLIDE_ANIMATION_MS}ms` }}
                >
                  {card}
                </Carousel.Item>
              ))}
            </Carousel>

            {/* Errors */}
            {submitError && (
              <Alert variant="danger" className="mb-3">
                {submitError}
              </Alert>
            )}

            {/* Footer actions */}
            <Row className="g-2">
              <Col xs="auto">
                <Button
                  variant="outline-secondary"
                  onClick={handlePrev}
                  disabled={atFirst || submitting}
                >
                  {LABELS.buttons.back}
                </Button>
              </Col>

              {!atSummary && !atLastDataStep && (
                <Col xs="auto">
                  <Button variant="primary" onClick={handleNext} disabled={submitting}>
                    {LABELS.buttons.next}
                  </Button>
                </Col>
              )}

              {!atSummary && atLastDataStep && (
                <Col xs="auto">
                  <Button
                    variant={requiredOk ? "success" : "secondary"}
                    onClick={handleFinish}
                    disabled={submitting || !requiredOk}
                  >
                    {submitting ? (
                      <>
                        <Spinner size="sm" animation="border" className="me-2" />
                        Finishing…
                      </>
                    ) : (
                      LABELS.buttons.finish
                    )}
                  </Button>
                </Col>
              )}

              {atSummary && (
                <>
                  <Col xs="auto">
                    <Button variant="outline-primary" onClick={() => setActiveIndex(0)} disabled={submitting}>
                      {LABELS.buttons.edit}
                    </Button>
                  </Col>
                  <Col xs="auto">
                    <Button variant="primary" disabled>
                      {LABELS.buttons.submit}
                    </Button>
                  </Col>
                </>
              )}
            </Row>
          </div>
        </Col>

        {/* Right rail (visual space for the tooltip to live) */}
        <Col lg={3} className="d-none d-lg-block">
          {/* Empty on purpose; overlay anchors to the span in header, appears here on the right */}
        </Col>
      </Row>

      {/* RIGHT-SIDE TOOLTIP (visible while invalid & not submitting) */}
      <Overlay
        target={rightTipAnchorRef.current}
        show={!requiredOk && !atSummary && !submitting}
        placement="right"
      >
        {(props) => (
          <Tooltip
            id="form-errors-tip"
            {...props}
            className="form-errors-tip"
            style={{ ...props.style, maxWidth: 320, textAlign: "left" }}
          >
            {tooltipContent}
          </Tooltip>
        )}
      </Overlay>

      {/* Styles */}
      <style>{`
        .carousel-item > .card { width: 100%; }

        /* Typewriter cursor */
        .tw-cursor {
          display: inline-block;
          margin-left: 1px;
          animation: tw-blink 1s step-end infinite;
        }
        @keyframes tw-blink {
          0%, 50% { opacity: 1; }
          50.01%, 100% { opacity: 0; }
        }

        /* Submission overlay feel (light grey + block interactions) */
        .wizard-submitting {
          position: relative;
          pointer-events: none;
          opacity: 0.6;
          filter: grayscale(20%);
        }

        /* Bootstrap valid/invalid outlines appear when classes applied */
        .is-valid { border-color: #198754 !important; }
        .is-invalid { border-color: #dc3545 !important; }

        /* Slightly larger tooltip for checklists */
        .tooltip.form-errors-tip .tooltip-inner {
          padding: 0.75rem 0.75rem;
        }
      `}</style>
    </Container>
  );
};
